'use client';

import {
  createElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type HTMLAttributes,
  type PointerEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { tooltipMotion, tooltip as tooltipTokens } from '@icebreaker/shared/design';
import { placeTooltip, type Placement, type TooltipSide } from './tooltip-placement';
import styles from './tooltip.module.css';

/**
 * İPUCU — kesilen metnin tamamını gösterir, başka bir şey yapmaz.
 *
 * Kaynak: `docs/design/ipucu/WarmBreak İpucu - Tooltip Bileşeni.dc.html`
 * (kullanıcı kararı 24.09: tarayıcının `title` ipucusu DEĞİL, tasarım
 * sisteminin ipucusu). Referansın sekiz kuralı:
 *
 *   1. YALNIZ KESİKSE. Açılmadan önce ölçülür (`scrollWidth > clientWidth`
 *      ya da `scrollHeight > clientHeight`); kesik değilse ipucu HİÇ çizilmez.
 *   2. KLAVYE. Tetikleyici ya da onu taşıyan bağlantı/düğme klavyeyle odak
 *      alınca gecikmesiz açılır; `Esc` kapatır, odak yerinde kalır.
 *   3. DOKUNMATİK YOK. `(hover: none)` ortamında hiç çizilmez.
 *   4. YALNIZ DÜZ METİN. İçerik tetikleyicinin `textContent`idir; kutu
 *      `pointer-events: none` taşır, üstüne gelinemez.
 *   5. ERİŞİLEBİLİRLİK. `role="tooltip"`, tetikleyicide `aria-describedby`.
 *   6. TEK SEFERDE BİR. Yeni ipucu açılınca öncekini gecikmesiz kapatır;
 *      başka bir ipucu açıkken geçiş gecikmesi kısadır (`tooltipMotion.delaySkip`).
 *   7. KAYDIRMA KAPATIR. Yeniden konumlanmaz.
 *   8. AZALTILMIŞ HAREKET. Kayma yok, yalnız anında görünür (CSS).
 *
 * Tavan: en çok 320 px ve 6 satır. Altı satırı aşan metin ipucunun işi
 * değildir (referans 03); bileşen o metinde ipucuyu açmaz, ikinci bir
 * kırpma yapmaz.
 */

interface OpenTooltip {
  readonly close: (immediate: boolean) => void;
}

/** Açık (ya da kaybolmakta olan) tek ipucu — modül düzeyinde, sayfa başına bir. */
let activeTooltip: OpenTooltip | null = null;

/** Metin gerçekten kesildi mi — açılmadan ÖNCE, tetikleyicinin kendisinde ölçülür. */
export function isTextTruncated(element: HTMLElement): boolean {
  return element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight;
}

function hoverUnavailable(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(hover: none)').matches
  );
}

/** Fare tıklamasıyla gelen odak ipucu açmaz; yalnız klavye odağı (`:focus-visible`). */
function keyboardFocused(element: Element): boolean {
  try {
    return element.matches(':focus-visible');
  } catch {
    return true;
  }
}

/** Yatay eksende esas alınan kutu: tetikleyicinin kendisi ya da taşıyan menü. */
export type TooltipAnchor = 'self' | 'menu';
const MENU = '[role="menu"]';

/** Tetikleyiciyi TAŞIYAN odaklanabilir ata (satır bağlantısı, menü düğmesi). */
const FOCUS_HOST = 'a[href], button, [tabindex]:not([tabindex="-1"])';

type Phase = 'measure' | 'shown' | 'leaving';

interface TooltipState {
  readonly text: string;
  readonly phase: Phase;
  readonly placement: Placement | null;
  /**
   * Ölçülen genişlik KİLİTLENİR. Ölçüldü (375 px): kutu sol kenarda ölçülüp
   * kaydırılınca tarayıcı satırları yeniden kırıp 320 → 317 px daraltıyor,
   * sağ pay 8 yerine 11 çıkıyordu.
   */
  readonly width: number | null;
}

export interface TruncationTooltip<T extends HTMLElement> {
  /** Kesilen öğeye yayılır: ref, fare/odak işleyicileri, `aria-describedby`. */
  readonly triggerProps: {
    readonly ref: (node: T | null) => void;
    readonly onPointerEnter: (event: PointerEvent<T>) => void;
    readonly onPointerLeave: (event: PointerEvent<T>) => void;
    readonly onFocus: (event: FocusEvent<T>) => void;
    readonly onBlur: (event: FocusEvent<T>) => void;
    readonly 'aria-describedby': string | undefined;
  };
  /** Gövdeye çizilen katman; kapalıyken `null` (DOM'da `role="tooltip"` yok). */
  readonly tooltip: ReactNode;
}

export function useTruncationTooltip<T extends HTMLElement>(
  /** Tercih edilen yön; sığmazsa referans sırasıyla sonraki yöne geçilir. */
  side: TooltipSide = 'top',
  /**
   * `menu`: yatay eksende tetikleyici değil, onu taşıyan açılır menü esas
   * alınır; ipucu menünün DIŞINA açılır, ok yine satırın hizasındadır
   * (referans 05-C: `right: calc(100% + 8px)`).
   */
  anchor: TooltipAnchor = 'self',
): TruncationTooltip<T> {
  const id = useId();
  const triggerRef = useRef<T | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hovering = useRef(false);
  const focused = useRef(false);
  /** Klavyeyle açıldıysa açıklama odaklanan öğeye de bağlanır. */
  const describedHost = useRef<Element | null>(null);
  /** Yerleşimin ölçüldüğü andaki tetikleyici konumu (kaydırma kararı için). */
  const placedAt = useRef<{ top: number; left: number } | null>(null);
  const [state, setState] = useState<TooltipState | null>(null);
  const handle = useRef<OpenTooltip | null>(null);

  const clearTimers = useCallback(() => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  }, []);

  const close = useCallback(
    (immediate: boolean) => {
      clearTimers();
      if (immediate) {
        if (activeTooltip === handle.current) activeTooltip = null;
        setState(null);
        return;
      }
      setState((current) => (current ? { ...current, phase: 'leaving' } : current));
      closeTimer.current = setTimeout(() => {
        closeTimer.current = null;
        if (activeTooltip === handle.current) activeTooltip = null;
        setState(null);
      }, tooltipMotion.duration);
    },
    [clearTimers],
  );
  handle.current ??= { close: (immediate) => close(immediate) };

  const show = useCallback(() => {
    openTimer.current = null;
    const element = triggerRef.current;
    if (!element || hoverUnavailable() || !isTextTruncated(element)) return;
    const text = (element.textContent ?? '').replace(/\s+/g, ' ').trim();
    if (!text) return;
    if (activeTooltip && activeTooltip !== handle.current) activeTooltip.close(true);
    activeTooltip = handle.current;
    setState({ text, phase: 'measure', placement: null, width: null });
  }, []);

  const open = useCallback(
    (delay: number) => {
      clearTimers();
      if (state && state.phase !== 'leaving') return;
      if (state?.phase === 'leaving') {
        setState({ ...state, phase: 'shown' });
        return;
      }
      if (delay === 0) show();
      else openTimer.current = setTimeout(show, delay);
    },
    [clearTimers, show, state],
  );

  /* Ölçüm: kutu görünmez (opaklık 0) çizildi; boyu alınır, yer hesaplanır. */
  useLayoutEffect(() => {
    if (state?.phase !== 'measure') return;
    const trigger = triggerRef.current;
    const tip = tipRef.current;
    if (!trigger || !tip) return;
    const content = tip.clientHeight - 2 * tooltipTokens.paddingY;
    const lineBox = tooltipTokens.fontSize * tooltipTokens.lineHeight;
    if (content > lineBox * tooltipTokens.maxLines + 1) {
      close(true);
      return;
    }
    const rect = trigger.getBoundingClientRect();
    placedAt.current = { top: rect.top, left: rect.left };
    const band = anchor === 'menu' ? trigger.closest(MENU)?.getBoundingClientRect() : undefined;
    const anchorRect = band
      ? { left: band.left, width: band.width, top: rect.top, height: rect.height }
      : rect;
    const box = tip.getBoundingClientRect();
    const placement = placeTooltip(
      anchorRect,
      { width: box.width, height: box.height },
      {
        width: document.documentElement.clientWidth,
        height: document.documentElement.clientHeight,
      },
      {
        offset: tooltipTokens.offset,
        edge: tooltipTokens.edge,
        arrow: tooltipTokens.arrow,
        radius: tooltipTokens.radius,
      },
      side,
    );
    setState((current) =>
      current ? { ...current, phase: 'shown', placement, width: box.width } : current,
    );
  }, [state, close, side, anchor]);

  /*
   * Açıkken: `Esc` kapatır (odak yerinde kalır), kaydırma ve yeniden boyut
   * kapatır; ipucu yeniden konumlanmaz.
   *
   * KAYDIRMA, TETİKLEYİCİ YERİNDEN OYNADIYSA kapatır. Ölçüldü (tur 48w,
   * Chromium): Tab odağı öğeyi görünür alana getirirken sayfa ODAKTAN ÖNCE
   * kayıyor, `scroll` olayı ise ipucu açıldıktan SONRA (bir sonraki karede)
   * bildiriliyor. Koşulsuz kapatma klavye ipucusunu açıldığı karede
   * kapatıyordu. Kullanıcı kaydırması tetikleyiciyi oynatır ve kapatır.
   */
  const isOpen = state !== null;
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // İlk `Esc` yalnız ipucuyu kapatır; altındaki menü ikinci basışta kapanır.
      event.stopPropagation();
      close(false);
    };
    const onScroll = () => {
      const placed = placedAt.current;
      const now = triggerRef.current?.getBoundingClientRect();
      if (
        placed &&
        now &&
        Math.abs(now.top - placed.top) < 0.5 &&
        Math.abs(now.left - placed.left) < 0.5
      )
        return;
      close(true);
    };
    const onResize = () => close(true);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [isOpen, close]);

  /* Klavyeyle açıldıysa odaklanan öğe (bağlantı, düğme) de açıklamayı taşır. */
  useEffect(() => {
    const host = describedHost.current;
    if (!isOpen || !host || host === triggerRef.current) return;
    const previous = host.getAttribute('aria-describedby');
    host.setAttribute('aria-describedby', previous ? `${previous} ${id}` : id);
    return () => {
      if (previous === null) host.removeAttribute('aria-describedby');
      else host.setAttribute('aria-describedby', previous);
    };
  }, [isOpen, id]);

  const onFocusIn = useCallback(
    (target: Element) => {
      if (!keyboardFocused(target)) return;
      focused.current = true;
      describedHost.current = target;
      open(0);
    },
    [open],
  );
  const onFocusOut = useCallback(() => {
    focused.current = false;
    if (!hovering.current) close(false);
  }, [close]);

  /* Tetikleyiciyi TAŞIYAN ata odak alırsa (menü düğmesi, satır bağlantısı). */
  const [focusHost, setFocusHost] = useState<Element | null>(null);
  useEffect(() => {
    if (!focusHost) return;
    const onIn = () => onFocusIn(focusHost);
    focusHost.addEventListener('focus', onIn);
    focusHost.addEventListener('blur', onFocusOut);
    return () => {
      focusHost.removeEventListener('focus', onIn);
      focusHost.removeEventListener('blur', onFocusOut);
    };
  }, [focusHost, onFocusIn, onFocusOut]);

  useEffect(
    () => () => {
      clearTimers();
      if (activeTooltip === handle.current) activeTooltip = null;
    },
    [clearTimers],
  );

  const ref = useCallback((node: T | null) => {
    triggerRef.current = node;
    setFocusHost(node?.parentElement?.closest(FOCUS_HOST) ?? null);
  }, []);

  const placement = state?.placement ?? null;
  const vertical = placement?.side === 'top' || placement?.side === 'bottom';
  const tooltip =
    state && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={tipRef}
            id={id}
            role="tooltip"
            className={styles.tip}
            data-side={placement?.side}
            data-open={state.phase === 'shown' ? 'true' : undefined}
            style={{
              left: placement?.left ?? 0,
              top: placement?.top ?? 0,
              width: state.width ?? undefined,
            }}
          >
            {state.text}
            <span
              className={styles.arrow}
              aria-hidden="true"
              style={
                placement
                  ? vertical
                    ? { left: placement.arrow }
                    : { top: placement.arrow }
                  : undefined
              }
            />
          </div>,
          document.body,
        )
      : null;

  return {
    triggerProps: {
      ref,
      onPointerEnter: (event) => {
        if (event.pointerType === 'touch') return;
        hovering.current = true;
        open(
          activeTooltip && activeTooltip !== handle.current
            ? tooltipMotion.delaySkip
            : tooltipMotion.delayIn,
        );
      },
      onPointerLeave: (event) => {
        if (event.pointerType === 'touch') return;
        hovering.current = false;
        if (openTimer.current) clearTimeout(openTimer.current);
        openTimer.current = null;
        if (focused.current || !state) return;
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => close(false), tooltipMotion.delayOut);
      },
      onFocus: (event) => onFocusIn(event.target),
      onBlur: onFocusOut,
      'aria-describedby': state ? id : undefined,
    },
    tooltip,
  };
}

type TruncatedTag = 'span' | 'p' | 'div' | 'b' | 'strong' | 'h2' | 'h3' | 'h4' | 'td';

export interface TruncatedTextProps
  extends Omit<
    HTMLAttributes<HTMLElement>,
    'onPointerEnter' | 'onPointerLeave' | 'onFocus' | 'onBlur' | 'title' | 'aria-describedby'
  > {
  /** Kesilen öğenin etiketi; kırpma çağıranın `className`inden gelir. */
  readonly as?: TruncatedTag;
  /** Varsayılan üst. Sağ kenardaki açılır menüde `left` (referans 05-C). */
  readonly side?: TooltipSide;
  /** `menu`: ipucu açılır menünün dışına açılır (referans 05-C). */
  readonly anchor?: TooltipAnchor;
  readonly children: ReactNode;
}

/**
 * Kırpılmış (üç nokta ya da satır kırpması) metni taşıyan öğe. Kırpma CSS'i
 * çağırandadır; bileşen yalnız "kesildiyse tamamını göster" davranışını ekler.
 * `title` özniteliği bilinçli olarak YOK (tarayıcı ipucusu reddedildi).
 *
 * Makine kapısı: `kesik-metin-ipucu.guard.spec.ts` — `apps/web/src` ve
 * `packages/ui/src` içinde kırpma bildiren her CSS sınıfı ya bu bileşenle
 * kullanılır ya da gerekçeli muafiyettedir.
 */
export function TruncatedText({
  as = 'span',
  side,
  anchor,
  children,
  ...rest
}: TruncatedTextProps) {
  const { triggerProps, tooltip } = useTruncationTooltip<HTMLElement>(side, anchor);
  return (
    <>
      {createElement(as, { ...rest, ...triggerProps }, children)}
      {tooltip}
    </>
  );
}
